/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect, useRef } from 'react';
import { toLocalIso } from './utils/localDate';
import type { ChangeEvent, InputHTMLAttributes, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronDown, AlertTriangle, ChevronLeft, CheckCircle2, X, ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';

/** Toast styles accepted by the global showToast() helper. */
export type ToastType = "success" | "error" | "warning" | "info";

/** Shape of a single toast rendered by ToastProvider. */
interface ToastItem {
  id: number;
  message?: string;
  type?: ToastType;
}

/**
 * A loosely-typed row from the Supabase `users` table (the `data` JSONB holds
 * the legacy Firestore profile fields: firstName/lastName/displayName/…).
 */
interface FormatUserRow {
  name?: string;
  email?: string;
  data?: {
    firstName?: string;
    lastName?: string;
    name?: string;
    displayName?: string;
    [key: string]: unknown;
  };
}

// --- UTILITY FUNCTIONS ---

/**
 * Dispatches a custom 'show-toast' event to trigger a global notification.
 * Use this to provide non-intrusive feedback to the user after actions (e.g., "Saved successfully").
 * It communicates with the {@link ToastProvider} via the window object.
 *
 * @param {string} message - The text to display in the toast.
 * @param {"success"|"error"|"warning"|"info"} [type="error"] - The visual style and icon of the toast.
 */
export const showToast = (message: string, type: ToastType = "error") => {
  window.dispatchEvent(new CustomEvent("show-toast", { detail: { message, type } }));
};

/**
 * Formats a date string or object into a compact German display format (DD.MM.YY).
 * Preferred for table cells and lists where horizontal space is limited.
 *
 * @param {string|Date} dateString - The date to format.
 * @returns {string} Formatted date string or the original input if invalid.
 */
export const formatDateDDMMYY = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '';

  let dateVal: string | Date = dateString;
  if (dateString && typeof dateString === 'object' && typeof (dateString as unknown as { toDate?: unknown }).toDate === 'function') {
    dateVal = (dateString as unknown as { toDate: () => Date }).toDate();
  } else if (dateString && typeof dateString === 'object' && typeof (dateString as unknown as { seconds?: unknown }).seconds === 'number') {
    dateVal = new Date((dateString as unknown as { seconds: number }).seconds * 1000);
  }

  const d = new Date(dateVal);
  if (isNaN(d.getTime())) {
    if (typeof dateString === 'object') return '';
    return dateString;
  }
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear().toString().slice(-2)}`;
};

/**
 * Formats a user's display name from available fields with correct priority.
 * Handles the case where the `name` column contains an email username (e.g. "constantin.krause")
 * instead of a proper display name ("Constantin Krause").
 *
 * Priority: data.firstName+lastName → name (if looks like real name) → data.name → data.displayName → email fallback
 *
 * @param {Object} userRow - A user row from the Supabase `users` table with optional `data` JSONB.
 * @returns {string} A formatted display name, or '—' if nothing is available.
 */
export const formatDisplayName = (userRow: FormatUserRow | null | undefined) => {
  if (!userRow) return '—';

  // 1. Prefer firstName + lastName from data JSONB (set by Supabase Auth)
  const fn = userRow.data?.firstName || '';
  const ln = userRow.data?.lastName || '';
  if (fn || ln) return `${fn} ${ln}`.trim();

  // 2. If name column contains spaces, it's likely a real name
  if (userRow.name && userRow.name.includes(' ')) return userRow.name;

  // 3. Try data.name or data.displayName
  if (userRow.data?.name && userRow.data.name.includes(' ')) return userRow.data.name;
  if (userRow.data?.displayName && userRow.data.displayName.includes(' ')) return userRow.data.displayName;

  // 4. Format email-username-style name: "constantin.krause" → "Constantin Krause"
  if (userRow.name && userRow.name !== userRow.email) {
    return userRow.name.split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  }

  // 5. Fall back to email username formatted
  if (userRow.email) {
    return userRow.email.split('@')[0].split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  }

  return '—';
};

// --- HELPER COMPONENTS ---

/**
 * A specialized date input that solves the "native vs. custom look" problem.
 * It displays a localized, formatted string (DD.MM.YY) in a read-only text field
 * while overlaying an invisible native date picker to ensure mobile-friendly interaction
 * and consistent styling across browsers.
 * 
 * @param {Object} props - Component props.
 * @param {string} props.value - The current date value in ISO format (YYYY-MM-DD).
 * @param {Function} props.onChange - Callback triggered on date selection.
 * @param {string} [props.min] - Minimum selectable date.
 * @param {string} [props.max] - Maximum selectable date.
 * @param {string} [props.className] - Tailwind classes for the outer container.
 * @param {string} [props.placeholder="DD.MM.YY"] - Hint text for empty state.
 * @param {boolean} [props.required] - HTML5 required validation flag.
 * @returns {JSX.Element}
 */
export function CustomDateInput({ value, onChange, min, max, className, placeholder, required, type = "date" }: InputHTMLAttributes<HTMLInputElement> & { type?: string }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDisplayDate = (dateStr: string | Date | Record<string, unknown> | null | undefined) => {
    if (!dateStr) return '';

    // Normalize non-string types to ISO date string (YYYY-MM-DD)
    let str: string = '';

    // Firestore Timestamp: { seconds, nanoseconds }
    if (typeof dateStr === 'object' && dateStr !== null && 'seconds' in dateStr) {
      str = toLocalIso(new Date((dateStr as { seconds: number }).seconds * 1000));
    }
    // Date object
    else if (dateStr instanceof Date) {
      str = toLocalIso(dateStr);
    }
    // dayjs object (has $d property which is a Date)
    else if (typeof dateStr === 'object' && dateStr !== null && '$d' in dateStr) {
      const dd = (dateStr as { $d?: unknown }).$d;
      str = dd instanceof Date ? toLocalIso(dd) : '';
    }
    // Already a string
    else if (typeof dateStr === 'string') {
      str = dateStr;
    }
    // Unknown type — log for debugging and return empty
    else {
      console.warn('[CustomDateInput] Unexpected date type:', typeof dateStr, dateStr);
      return '';
    }

    if (!str) return '';

    if (type === "datetime-local" && str.includes('T')) {
      const [d, t] = str.split('T');
      const parts = d.split('-');
      if (parts.length === 3) {
        const yy = parts[0].substring(2, 4);
        return `${parts[2]}.${parts[1]}.${yy} ${t}`;
      }
      return str;
    }
    const parts = str.split('-');
    if (parts.length === 3) {
      const yy = parts[0].substring(2, 4);
      return `${parts[2]}.${parts[1]}.${yy}`;
    }
    return str;
  };
  const displayValue = formatDisplayDate(value as string | undefined);
  const dateRef = useRef<HTMLInputElement | null>(null);

  const openPicker = () => {
    if (!dateRef.current) return;
    try {
      if (typeof dateRef.current.showPicker === 'function') {
        dateRef.current.showPicker();
      } else {
        dateRef.current.focus();
      }
    } catch {
      // showPicker() can throw SecurityError/InvalidStateError in some contexts
      // (e.g. not triggered by a user gesture, or input not ready). Fall back
      // to focusing the input so the native picker can still be opened.
      dateRef.current.focus();
    }
  };

  const defaultPlaceholder = lang === 'de-DE'
    ? t('utils.dateFormatDE', 'DD.MM.YY')
    : t('utils.dateFormatUS', 'MM/DD/YY');

  return (
    <div className="relative inline-flex items-center w-full" onClick={openPicker}>
      <input type="text" readOnly value={displayValue} placeholder={placeholder != null ? placeholder : defaultPlaceholder} className={`${className} cursor-pointer w-full relative z-10`} />
      <input ref={dateRef} type={type} value={value} min={min} max={max} onChange={onChange} required={required} lang={lang} className="custom-date-input absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" />
    </div>
  );
}


/**
 * A sophisticated multi-select dropdown designed for high-density UI.
 * It provides a compact summary of selections to save screen real estate,
 * while offering a checkbox-based selection list in an animated popover.
 * Ideal for filtering long lists (e.g., users, status codes, or categories).
 * 
 * @param {Object} props - Component props.
 * @param {Array<string>} props.options - List of available options.
 * @param {Array<string>} [props.selected=[]] - List of currently selected options.
 * @param {Function} props.onChange - Callback triggered when the selection changes.
 * @param {string} props.placeholder - Context-specific placeholder (e.g., "Zuweisung").
 * @param {React.ElementType} [props.icon] - Optional Lucide icon to display in the trigger.
 * @param {string} [props.className="w-full sm:w-auto"] - Additional CSS classes for the container.
 * @param {Function} [props.translateLabel] - Optional callback to translate or format option labels.
 * @param {boolean} [props.searchable=false] - Optional flag to enable search bar.
 * @param {string} [props.emptyLabel] - Optional label to show when nothing is selected.
 * @returns {JSX.Element}
 */
export function MultiSelectDropdown({
  options,
  selected = [],
  onChange,
  placeholder,
  icon: Icon,
  className = "w-full sm:w-auto",
  translateLabel = undefined,
  searchable = false,
  emptyLabel = undefined
}: {
  options: string[];
  selected?: string[];
  onChange: (selected: string[]) => void;
  placeholder: string;
  icon?: LucideIcon | null;
  className?: string;
  translateLabel?: ((val: string) => string) | undefined;
  searchable?: boolean;
  emptyLabel?: string | undefined;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const inTrigger = ref.current && ref.current.contains(target);
      const inPanel = panelRef.current && panelRef.current.contains(target);
      if (!inTrigger && !inPanel) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate panel position when opened or on scroll/resize so the portaled
  // panel stays anchored to the trigger button even inside virtualized rows.
  useEffect(() => {
    if (!isOpen) return;
    const updatePos = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [isOpen]);

  const toggleOption = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter(i => i !== opt));
    else onChange([...selected, opt]);
  };

  const getDisplayLabel = (val: string) => translateLabel ? translateLabel(val) : val;

  const isPersonList = placeholder && (
    placeholder.toLowerCase().includes('person') ||
    placeholder.toLowerCase().includes('zuweisung') ||
    placeholder.toLowerCase().includes('verantwortlich') ||
    placeholder.toLowerCase().includes('zuständig') ||
    placeholder.toLowerCase().includes('assignee')
  );

  let label = emptyLabel || (isPersonList ? placeholder : t('common.allOf', 'Alle {{placeholder}}', { placeholder }));
  if (selected.length === 1) label = getDisplayLabel(selected[0]);
  else if (selected.length > 1) {
    // If it looks like a list of names (Personen/Zuweisung/Verantwortlich/Zuständig), show names
    if (isPersonList) {
      label = selected.map(getDisplayLabel).join(', ');
    } else {
      label = t('common.selectedCount', '{{count}} {{placeholder}} gewählt', { count: selected.length, placeholder });
    }
  }

  const filteredOptions = searchable
    ? options.filter(opt => getDisplayLabel(opt).toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  return (
    <div className={`relative min-w-0 max-w-full ${className}`} ref={ref}>
      <button ref={buttonRef} onClick={() => setIsOpen(!isOpen)} className={`relative flex items-center justify-between pl-3 pr-8 py-2 bg-white dark:bg-slate-950/70 border border-slate-200 dark:border-slate-600/80 rounded-xl text-xs font-bold text-slate-800 dark:text-white focus:border-[#859F4A] dark:focus:border-[#859F4A] focus:ring-2 focus:ring-[#859F4A]/25 outline-hidden cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-all w-full min-w-[140px] h-[38px] overflow-hidden shadow-2xs`}>
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={16} className="text-[#859F4A] shrink-0" />}
          <span className="truncate">{label}</span>
        </div>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-400 pointer-events-none" />
      </button>

      {isOpen && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, minWidth: Math.max(panelPos.width, 200), /* Above the highest modal layer (z-[9999999]) — the portaled panel otherwise opens BEHIND modals */ zIndex: 99999999 }}
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl flex flex-col max-h-72 overflow-hidden animate-in fade-in slide-in-from-top-2"
        >
          {/* Header area: Search input & Quick Actions (Select All / Clear) */}
          {(searchable || selected.length > 0 || selected.length < options.length) && (
            <div className="shrink-0 p-2.5 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-2 bg-white dark:bg-slate-800">
              {searchable && (
                <input
                  type="text"
                  placeholder={t('common.search', 'Suchen') + '...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-xs px-3 py-1.5 bg-slate-50 dark:bg-slate-950/80 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-600/80 rounded-lg outline-hidden focus:border-[#859F4A] dark:focus:border-[#859F4A] focus:ring-2 focus:ring-[#859F4A]/25 placeholder:text-slate-400 dark:placeholder:text-slate-500 shadow-2xs transition-all"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {(selected.length > 0 || selected.length < options.length) && (
                <div className="flex items-center justify-between gap-2 px-1">
                  {selected.length < options.length ? (
                    <button
                      type="button"
                      onClick={() => onChange(options)}
                      className="text-xs text-[#859F4A] hover:text-[#6a7f3b] font-bold transition-all uppercase tracking-wider bg-transparent border-none p-0 outline-hidden cursor-pointer"
                    >
                      {t('common.selectAll', 'Alle auswählen')}
                    </button>
                  ) : <span />}
                  {selected.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onChange([])}
                      className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 font-bold transition-all uppercase tracking-wider bg-transparent border-none p-0 outline-hidden cursor-pointer"
                    >
                      {t('common.clearSelection', 'Auswahl aufheben')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Options list: Dedicated scroll area */}
          <div className="overflow-y-auto flex-1 py-1 max-h-56 bg-white dark:bg-slate-800">
            {filteredOptions.map(opt => (
              <label
                key={opt}
                className="flex items-center gap-3 px-3.5 py-2 hover:bg-[#859F4A]/15 dark:hover:bg-[#859F4A]/20 cursor-pointer text-xs font-medium text-slate-800 dark:text-slate-200 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggleOption(opt)}
                  className="rounded border-slate-300 dark:border-slate-600 text-[#859F4A] focus:ring-[#859F4A] w-3.5 h-3.5 cursor-pointer accent-[#859F4A]"
                />
                <span className="truncate">{getDisplayLabel(opt)}</span>
              </label>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500 text-center">
                {t('common.noOptions', 'Keine Optionen')}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * The central hub for application-wide notifications.
 * It decouples the triggering of alerts from the UI components by listening
 * to global window events, ensuring that any part of the app can show a toast
 * without prop-drilling or complex state management.
 * 
 * To trigger a toast, use the {@link showToast} utility.
 * 
 * @returns {JSX.Element|null}
 */
export function ToastProvider() {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const handleToast = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const newToast: ToastItem = { id: Date.now() + Math.random(), ...detail };
      setToasts(prev => [...prev, newToast]);

      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== newToast.id));
      }, 5000);
    };

    window.addEventListener("show-toast", handleToast);
    return () => window.removeEventListener("show-toast", handleToast);
  }, []);

  if (toasts.length === 0) return null;

  return createPortal(
    <div className='fixed bottom-6 right-6 z-999999 flex flex-col gap-3 pointer-events-none'>
      {toasts.map(toast => (
        <div key={toast.id} className={`pointer-events-auto flex items-start gap-3 p-4 rounded-2xl shadow-2xl bg-white/95 border animate-in slide-in-from-bottom-5 fade-in w-80 ${toast.type === "error" ? "border-red-200" : "border-accent/30"}`}>
          {toast.type === "error" ? (
            <AlertTriangle className='text-red-500 shrink-0 mt-0.5' size={18} />
          ) : (
            <CheckCircle2 className='text-accent shrink-0 mt-0.5' size={18} />
          )}
          <div className='flex-1'>
            <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${toast.type === "error" ? "text-red-500" : "text-accent"}`}>
              {toast.type === "error" ? t('toasts.error', 'Fehlgeschlagen') : t('toasts.success', 'Erfolgreich')}
            </p>
            <p className='text-sm font-medium text-fg leading-tight'>{toast.message}</p>
          </div>
          <button onClick={() => setToasts(prev => prev.filter(item => item.id !== toast.id))} className='text-muted hover:text-fg transition-all'>
            <X size={16} />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}

/**
 * A high-impact visual card for key performance indicators (KPIs).
 * Designed to provide an at-a-glance overview of business metrics, including
 * trend indicators (percentage change) and actionable warnings for data anomalies.
 * 
 * @param {Object} props - Component props.
 * @param {string} props.title - The label for the metric.
 * @param {string|number} props.value - The main metric value.
 * @param {string} [props.change] - Optional text for displaying change (e.g., "+5%").
 * @param {string} [props.sub] - Optional sub-text displayed below the value.
 * @param {string} [props.color] - Color theme for the icon badge (green, blue, purple, amber).
 * @param {boolean} [props.warning] - If true, displays a warning triangle icon.
 * @param {Function} [props.onWarningClick] - Callback triggered when the warning icon is clicked.
 * @param {React.ElementType} [props.icon] - Lucide icon component to display in the badge.
 * @returns {JSX.Element}
 */
export function KpiCard({ title, value, change, sub, color, warning, onWarningClick, icon: IconComp }: {
  title: string;
  value: string | number;
  change?: string;
  sub?: string;
  color?: string;
  warning?: boolean;
  onWarningClick?: (() => void) | undefined;
  icon?: LucideIcon | null;
}) {
  const getBadgeClass = (color?: string) => {
    switch (color) {
      case 'green': return 'bg-[#859F4A]/10 text-[#859F4A] border border-[#859F4A]/20 dark:bg-emerald-950/50 dark:text-[#A3C066] dark:border-emerald-800/50';
      case 'blue': return 'bg-blue-50 text-blue-600 border border-blue-100 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-800/50';
      case 'purple': return 'bg-purple-50 text-purple-600 border border-purple-100 dark:bg-purple-950/50 dark:text-purple-400 dark:border-purple-800/50';
      case 'amber': return 'bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800/50';
      case 'cyan': return 'bg-cyan-50 text-cyan-700 border border-cyan-100 dark:bg-cyan-950/50 dark:text-cyan-400 dark:border-cyan-800/50';
      case 'indigo': return 'bg-indigo-50 text-indigo-600 border border-indigo-100 dark:bg-indigo-950/50 dark:text-indigo-400 dark:border-indigo-800/50';
      default: return 'bg-[#859F4A]/10 text-[#859F4A] border border-[#859F4A]/20 dark:bg-emerald-950/50 dark:text-[#A3C066] dark:border-emerald-800/50';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800/95 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl p-2.5 sm:p-3 shadow-xs hover:shadow-sm hover:border-[#859F4A]/50 dark:hover:border-[#859F4A]/50 transition-all duration-150 flex flex-col justify-between">
      <div className="flex justify-between items-center mb-1.5">
        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${getBadgeClass(color)}`}>
          {IconComp && <IconComp size={15} strokeWidth={2} />}
        </div>
        <div className="flex gap-1.5">
          {change && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800/50">
              {change}
            </span>
          )}
          {warning && (
            <button onClick={onWarningClick} className="p-1 hover:bg-amber-50 dark:hover:bg-amber-950/40 rounded-lg text-amber-500 transition-colors cursor-pointer">
              <AlertTriangle size={14} />
            </button>
          )}
        </div>
      </div>
      <div>
        <p className="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5 leading-tight">{title}</p>
        <p className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight leading-tight truncate">{value}</p>
        {sub && <p className="text-[9px] sm:text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">{sub}</p>}
      </div>
    </div>
  );
}

/**
 * A data-integrity component used for visual reconciliation.
 * It highlights discrepancies between a "source of truth" (target) and the
 * "actual state" (current), making it easy for users to identify and correct
 * data entry errors or sync issues.
 * 
 * @param {Object} props - Component props.
 * @param {string|number} props.target - The expected/target value.
 * @param {string|number} props.current - The actual/current value.
 * @param {boolean} props.isTargetRow - If true, treats this as the source of truth (no diff shown).
 * @param {boolean} [props.bold=false] - Whether to use bold text for the value.
 * @returns {JSX.Element}
 */
export const DiffValue = ({ target, current, isTargetRow, bold = false }: {
  target: string | number | null | undefined;
  current: string | number | null | undefined;
  isTargetRow?: boolean;
  bold?: boolean;
}) => {
  const { t: tr } = useTranslation();
  const t = (target || '').toString();
  const c = (current || '').toString();

  if (isTargetRow || t === c) {
    return <span className={`${bold ? 'font-bold text-fg' : 'font-medium text-fg wrap-break-word'}`}>{c || '-'}</span>;
  }

  return (
    <div className="flex flex-col gap-0.5 my-0.5 items-start">
      <span className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded font-mono text-[11px] whitespace-pre-wrap wrap-break-word shadow-xs leading-tight max-w-full">
        {c === '' ? tr('utils.empty', '<leer>') : `"${c}"`}
      </span>
      <span className="text-[11px] text-muted font-mono leading-tight bg-bg px-1 rounded truncate max-w-full">
        {tr('utils.target', 'Ziel: ')}{t === '' ? tr('utils.empty', '<leer>') : `"${t}"`}
      </span>
    </div>
  );
};


// HELPER FOR DASHBOARD
/**
 * Standardized pagination controls for large datasets.
 * Encapsulates page size selection and navigation logic to provide a
 * consistent user experience across all tables and lists in the application.
 * 
 * @param {Object} props - Component props.
 * @param {number} props.pageSize - Number of items per page.
 * @param {Function} props.setPageSize - Callback to update page size.
 * @param {number} props.currentPage - The current active page index (1-based).
 * @param {Function} props.setCurrentPage - Callback to update current page.
 * @param {number} props.totalPages - Total number of pages available.
 * @param {string} [props.extraClasses=""] - Additional CSS classes.
 * @returns {JSX.Element}
 */
export const PaginationControls = ({ currentPage, setCurrentPage, totalPages, extraClasses = "" }: {
  currentPage: number;
  setCurrentPage: (n: number) => void;
  totalPages: number;
  extraClasses?: string;
}) => (
  <div className={`flex flex-col sm:flex-row justify-end items-center px-6 py-4 lg:px-0 lg:py-0 bg-bg lg:bg-transparent rounded-2xl lg:rounded-none gap-4 ${extraClasses}`}>
    <div className="flex items-center gap-4 lg:gap-2 text-xs font-medium">
      <div className="flex gap-1">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="p-2 lg:p-1 bg-surface lg:bg-transparent lg:border-0 rounded-xl lg:rounded-md text-muted hover:text-fg hover:bg-bg lg:hover:bg-surface disabled:opacity-50 disabled:hover:bg-transparent transition-all"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(currentPage + 1)}
          className="p-2 lg:p-1 bg-surface lg:bg-transparent lg:border-0 rounded-xl lg:rounded-md text-muted hover:text-fg hover:bg-bg lg:hover:bg-surface disabled:opacity-50 disabled:hover:bg-transparent transition-all"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  </div>
);

/**
 * A layout utility for optimizing mobile space.
 * It converts complex desktop headers/actions into a collapsible unit on mobile,
 * ensuring that primary content remains the focus while keeping secondary actions
 * accessible via a toggle.
 * 
 * @param {Object} props - Component props.
 * @param {string} props.title - The main title of the header.
 * @param {string} [props.subtitle] - Optional subtitle (shown in uppercase).
 * @param {React.ElementType} [props.icon] - Optional icon component.
 * @param {React.Node} [props.extra] - Optional extra content to show in the toggle bar.
 * @param {React.Node} props.children - The content to be collapsed/expanded.
 * @param {string} [props.stickyOffset="top-0"] - Sticky position offset (e.g., "top-16").
 * @returns {JSX.Element}
 */
export function MobileCollapsibleHeader({ title, subtitle, icon: Icon, extra, children, stickyOffset = "top-0", contentClassName }: {
  title: string;
  subtitle?: string;
  icon?: LucideIcon | null;
  extra?: ReactNode;
  children: ReactNode;
  stickyOffset?: string; contentClassName?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024;
    }
    return false;
  });



  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isOpen]);

  return (
    <>
      {/* Mobile Toggle Bar */}
      <div className={`lg:hidden sticky ${stickyOffset} z-30 bg-bg/95 py-2 mb-4`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-white dark:bg-surface border border-[#E2E8F0] dark:border-border rounded-[20px] py-3.5 px-6 flex items-center justify-between shadow-[0_2px_4px_rgba(0,0,0,0.03)] active:scale-[0.98] transition-all text-left cursor-pointer"
        >
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="w-8 h-8 flex items-center justify-center text-[#859F4A] shrink-0">
                <Icon size={20} strokeWidth={1.5} />
              </div>
            )}
            <div>
              <div className="font-bold text-[#1E293B] dark:text-foreground text-[15px]">{title}</div>
              {subtitle && <div className="text-[10px] font-bold text-[#6B7B90] dark:text-muted uppercase tracking-[0.15em] mt-0.5">{subtitle}</div>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {extra && <div onClick={(e) => e.stopPropagation()}>{extra}</div>}
            <ChevronDown size={20} className="text-[#6B7B90] dark:text-muted transition-transform duration-300 shrink-0" style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
          </div>
        </button>
      </div>

      {/* Desktop Layout (always in-place) */}
      {!isMobile && (
        <div className={contentClassName ?? "hidden lg:flex flex-col gap-4"}>
          {children}
        </div>
      )}

      {/* Mobile Layout - Bottom Sheet Drawer (Portal) */}
      {isMobile && isOpen && createPortal(
        <div className="ui-v1">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 z-99999 animate-in fade-in"
            onClick={() => setIsOpen(false)}
          />

          {/* Bottom Sheet Drawer */}
          <div className="fixed bottom-0 left-0 right-0 z-100000 bg-white dark:bg-surface border-t border-[#E2E8F0] dark:border-border rounded-t-[20px] shadow-2xl p-6 max-h-[85vh] overflow-y-auto flex flex-col gap-6 animate-in slide-in-from-bottom">
            {/* Drawer Header */}
            <div className="flex flex-col gap-4 shrink-0 pb-2 border-b border-[#E2E8F0] dark:border-border">
              {/* Drag Handle */}
              <div className="w-12 h-1 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto" />

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {Icon && (
                    <div className="w-10 h-10 flex items-center justify-center text-[#859F4A] shrink-0">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="text-left">
                    <div className="font-bold text-[#1E293B] dark:text-foreground text-[15px]">{title}</div>
                    {subtitle && <div className="text-[10px] font-bold text-[#6B7B90] dark:text-muted uppercase tracking-[0.15em] mt-0.5">{subtitle}</div>}
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-10 h-10 border border-[#E2E8F0] dark:border-border rounded-full flex items-center justify-center bg-white dark:bg-surface hover:bg-slate-50 dark:hover:bg-slate-800 transition-all shadow-[0_2px_4px_rgba(0,0,0,0.03)] active:scale-95 text-[#475569] dark:text-foreground shrink-0 cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Main Content (Children) */}
            <div className="flex flex-col gap-6 overflow-y-auto">
              {children}
            </div>

          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Triggers a global notification log in Firestore and alerts matching users via
 * Background FCM push notification through the serverless backend.
 * 
 * @param {Function} getToken - Supabase Auth session token getter.
 * @param {Object} notificationData - The notification body payload.
 * @returns {Promise<Object>} API response object.
 */
export async function sendPushNotification(getToken: () => Promise<string | null>, notificationData: Record<string, unknown>) {
  try {
    let token = null;
    if (typeof getToken === 'function') {
      try {
        token = await getToken();
      } catch (e) {
        console.warn("[utils] Failed to get auth token in sendPushNotification:", e);
      }
    }
    const response = await fetch('/api/services?action=send-push', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(notificationData)
    });
    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Server error');
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to send notification via API:", error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Normalizes a list of user documents / names by extracting the best display
 * name per person. Used to build chat-mention / assignment lists so that
 * Supabase-migrated users (e.g. "Estella Krist") and legacy Firebase users
 * (e.g. "Estella") are not shown twice for the same human.
 *
 * Strategy:
 *  - Accepts raw user documents OR pre-extracted name strings.
 *  - Deduplicates exact full-name matches (case-insensitive), keeping the
 *    more complete (longer) spelling.
 *  - Collapses a shorter name only when it is a word-boundary prefix of a
 *    longer name (e.g. "Estella" vs "Estella Krist"), which is the migration
 *    duplicate case. Two distinct people sharing a first name (e.g.
 *    "Simone Müller" vs "Simone Schmidt") are NOT prefixes of each other and
 *    are both kept.
 *  - Returns a sorted, deduplicated array of display names.
 *
 * @param {Array<Object|string>} entries - User docs or plain name strings.
 * @returns {string[]} Sorted unique display names.
 */
export const dedupeUserNamesByFirstName = (entries: Array<Record<string, unknown> | string>) => {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const names: string[] = [];
  for (const entry of entries) {
    let raw: unknown;
    if (typeof entry === 'string') {
      raw = entry;
    } else if (entry && typeof entry === 'object') {
      const data: unknown = typeof (entry as { data?: unknown }).data === 'function' ? (entry as { data: () => unknown }).data() : entry;
      const d = data as { name?: unknown; firstName?: unknown; displayName?: unknown } | null;
      raw = d?.name || d?.firstName || d?.displayName || '';
    }
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    names.push(trimmed);
  }

  // Deduplicate exact full-name matches (case-insensitive), keeping the
  // longer spelling when the same name appears with different casing.
  const byFullName = new Map<string, string>();
  for (const name of names) {
    const key = name.toLowerCase();
    const existing = byFullName.get(key);
    if (!existing || name.length > existing.length) {
      byFullName.set(key, name);
    }
  }

  // Drop a shorter name that is a word-boundary prefix of a longer name
  // (migration duplicate: "Estella" -> "Estella Krist"). Distinct users who
  // merely share a first name are preserved.
  const unique = Array.from(byFullName.values());
  const result = unique.filter((name) => {
    const lower = name.toLowerCase();
    return !unique.some(
      (other) =>
        other !== name &&
        other.length > name.length &&
        other.toLowerCase().startsWith(lower + ' ')
    );
  });

  return result.sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
};
